/** Reading a comparison of two laps for the cards that draw it.
 *
 * The comparison itself is `lib/analysis`'s: the logger's arithmetic, ported,
 * run in the browser on the two lap documents already on screen. It answers
 * in the logger's own shape — the shape its `/api/analysis/compare` answers —
 * so that the port can be tested against the logger's answers and so that a
 * card here draws the same figure the logger's Analysis view draws. This
 * module is the step between that document and a chart: the domain of a
 * delta trace, the geometry of its area, the corner rows sorted by what they
 * cost, the g-g scatter in g rather than raw units.
 *
 * Two conventions run through everything below and are the logger's, not
 * this screen's: a **positive delta is time lost** against the reference, and
 * every distance is **metres along the reference lap's line**, which is what
 * lining the laps up by position gives, so a corner at 1 240 m is the same
 * corner on both.
 */

import type {
  AccelCalibration,
  CompareLap,
  CompareResult,
  Corner,
  CornerRow,
  DeltaSeries,
  Deviation,
  GgExtremes,
  LapEvent,
} from '@/lib/analysis';

// ── the comparison, as the port answers it ──────────────────────────────────
//
// The port's own types, under the names the cards use. A corner here is one
// placed on the reference lap's distance axis — the circuit's authored corner
// when it has one, or one detected on the reference — and a report row is one
// lap's figures through one of the reference's corner windows.

export type CornerMark = Corner;
export type ReportRow = CornerRow;
export type ComparedLap = CompareLap;
export type Comparison = CompareResult;
export type GGExtremes = GgExtremes;
export type { AccelCalibration, DeltaSeries, Deviation };

/** The two ids a comparison on this screen is made of. The port keys its
 *  answer by the id as a string. */
export const CHOSEN = 1;
export const REFERENCE = 2;

/** A chassis event with the three things a chart needs, from the logger's
 *  loosely typed record; one without them is not drawn. */
export interface EventBand {
  type: string;
  from: number;
  to: number;
  wheels: string;
  severity: number | null;
}

export function eventBands(events: readonly LapEvent[] | undefined, type: string): EventBand[] {
  const out: EventBand[] = [];
  for (const event of events ?? []) {
    if (event['type'] !== type) continue;
    const from = event['start_dist'];
    const to = event['end_dist'];
    if (typeof from !== 'number' || typeof to !== 'number') continue;
    const wheels = Array.isArray(event['wheels'])
      ? event['wheels'].filter((wheel): wheel is string => typeof wheel === 'string').join(', ')
      : '';
    const severity = typeof event['severity'] === 'number' ? event['severity'] : null;
    out.push({ type, from, to: Math.max(to, from + 2), wheels, severity });
  }
  return out;
}

// ── chart geometry ──────────────────────────────────────────────────────────

export interface Box {
  width: number;
  height: number;
  padTop: number;
  padBottom: number;
}

export const DELTA_BOX: Box = { width: 400, height: 96, padTop: 8, padBottom: 8 };
export const PEDAL_BOX: Box = { width: 400, height: 48, padTop: 3, padBottom: 1 };

function finite(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function distanceX(metres: number, maxDistance: number, box: Box): number {
  return maxDistance > 0 ? (metres / maxDistance) * box.width : 0;
}

export interface DeltaDomain {
  maxDistance: number;
  /** Seconds, symmetric about zero so the zero line sits where the eye
   *  expects it and a lap that only lost time is not drawn as a full box. */
  limit: number;
}

/** A delta trace's domain. Null for a trace too short to say anything. */
export function deltaDomain(delta: DeltaSeries | null | undefined): DeltaDomain | null {
  if (!delta || delta.dist.length < 2) return null;
  let maxDistance = 0;
  let worst = 0;
  for (let i = 0; i < delta.dist.length; i += 1) {
    const metres = delta.dist[i];
    const ms = delta.delta_ms[i];
    if (finite(metres) && metres > maxDistance) maxDistance = metres;
    if (finite(ms) && Math.abs(ms) > worst) worst = Math.abs(ms);
  }
  if (maxDistance <= 0) return null;
  // A tenth is the least a chart should be able to show, and the limit is
  // rounded up to a tidy figure so the labels either side read as one.
  const seconds = Math.max(worst / 1000, 0.1);
  const steps = [0.1, 0.2, 0.25, 0.5, 1, 2, 3, 5, 10, 20, 30, 60];
  const limit = steps.find((step) => step >= seconds) ?? Math.ceil(seconds);
  return { maxDistance, limit };
}

export function deltaY(ms: number, domain: DeltaDomain, box: Box = DELTA_BOX): number {
  const usable = box.height - box.padTop - box.padBottom;
  const share = Math.max(-1, Math.min(1, ms / 1000 / domain.limit));
  return box.padTop + usable / 2 - (share * usable) / 2;
}

/** Where zero sits, which is also the baseline every area is closed to. */
export function zeroY(domain: DeltaDomain, box: Box = DELTA_BOX): number {
  return deltaY(0, domain, box);
}

/** The trace as a line, and the same trace closed to the zero line as an
 *  area. The area is drawn twice, clipped above and below zero, so time lost
 *  and time gained can wear their own colours without the path being split
 *  at every crossing. */
export function deltaPaths(
  delta: DeltaSeries,
  domain: DeltaDomain,
  box: Box = DELTA_BOX,
): { line: string; area: string } {
  const points: string[] = [];
  let first: string | null = null;
  let last: string | null = null;
  for (let i = 0; i < delta.dist.length; i += 1) {
    const metres = delta.dist[i];
    const ms = delta.delta_ms[i];
    if (!finite(metres) || !finite(ms)) continue;
    const x = distanceX(metres, domain.maxDistance, box).toFixed(1);
    const y = deltaY(ms, domain, box).toFixed(1);
    points.push(`${x},${y}`);
    first ??= x;
    last = x;
  }
  if (points.length < 2 || first === null || last === null) return { line: '', area: '' };
  const zero = zeroY(domain, box).toFixed(1);
  return {
    line: points.join(' '),
    area: `M${first},${zero} L${points.join(' L')} L${last},${zero} Z`,
  };
}

/** A pedal or speed channel against the axis, as an SVG points list on a
 *  0–`top` scale. Gaps in the channel break the line rather than bridge it. */
export function channelPolyline(
  dist: readonly number[],
  values: readonly number[] | undefined,
  maxDistance: number,
  top: number,
  box: Box = PEDAL_BOX,
): string {
  if (!values || values.length === 0 || maxDistance <= 0) return '';
  const usable = box.height - box.padTop - box.padBottom;
  const out: string[] = [];
  const n = Math.min(dist.length, values.length);
  for (let i = 0; i < n; i += 1) {
    const metres = dist[i];
    const value = values[i];
    if (!finite(metres) || !finite(value)) continue;
    const x = distanceX(metres, maxDistance, box);
    const y = box.height - box.padBottom - (Math.max(0, Math.min(top, value)) / top) * usable;
    out.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  }
  return out.join(' ');
}

/** The furthest either lap's series reaches, which is the axis both share. */
export function sharedDistance(laps: ReadonlyArray<ComparedLap | null | undefined>): number {
  let most = 0;
  for (const lap of laps) {
    const dist = lap?.series['dist'];
    const end = dist?.[dist.length - 1];
    if (finite(end) && end > most) most = end;
  }
  return most;
}

// ── corners along the axis ──────────────────────────────────────────────────

export interface CornerTick {
  n: number;
  x: number;
  /** Whether there is room to write the number: neighbours too close keep
   *  their tick and lose their label. */
  labelled: boolean;
}

/** Corner numbers along the distance axis, labels thinned where they would
 *  collide. `minGap` is in the box's own units. */
export function cornerTicks(
  corners: readonly CornerMark[] | undefined,
  maxDistance: number,
  box: Box = DELTA_BOX,
  minGap = 14,
): CornerTick[] {
  if (!corners || maxDistance <= 0) return [];
  const ticks = [...corners]
    .filter((corner) => finite(corner.apex_dist))
    .sort((a, b) => a.apex_dist - b.apex_dist)
    .map((corner) => ({ n: corner.n, x: distanceX(corner.apex_dist, maxDistance, box), labelled: true }));
  let lastLabel = Number.NEGATIVE_INFINITY;
  for (const tick of ticks) {
    if (tick.x - lastLabel < minGap) {
      tick.labelled = false;
    } else {
      lastLabel = tick.x;
    }
  }
  return ticks;
}

// ── the corner report ───────────────────────────────────────────────────────

export interface CornerCompareRow {
  n: number;
  name: string;
  direction: string | null;
  angle: number | null;
  /** Metres along the reference's line, for zooming and for the "before the
   *  apex" arithmetic. */
  apexDist: number | null;
  row: ReportRow;
  ref: ReportRow | null;
  /** This lap's time through the corner minus the reference's. Null when
   *  the reference did not drive the window, or this is the reference. */
  lostMs: number | null;
  minSpeedDelta: number | null;
  exitSpeedDelta: number | null;
}

/**
 * One row per corner this lap drove, sorted by what it cost: the biggest
 * loss first, gains last, and corners without a reference figure in track
 * order after them. Comparing the reference against itself gives rows with
 * no deltas, which is the reference's own report.
 */
export function cornerCompareRows(
  corners: readonly CornerMark[] | undefined,
  lap: ComparedLap | null | undefined,
  reference: ComparedLap | null | undefined,
  isReference: boolean,
): CornerCompareRow[] {
  if (!lap) return [];
  const marks = new Map((corners ?? []).map((corner) => [corner.n, corner]));
  const refRows = new Map((reference?.corner_report ?? []).map((row) => [row.n, row]));
  const rows = (lap.corner_report ?? []).map((row): CornerCompareRow => {
    const mark = marks.get(row.n);
    const ref = isReference ? null : (refRows.get(row.n) ?? null);
    return {
      n: row.n,
      name: mark?.name ?? '',
      direction: mark?.direction ?? null,
      angle: mark?.angle_deg ?? null,
      apexDist: mark?.apex_dist ?? null,
      row,
      ref,
      lostMs: ref ? row.time_ms - ref.time_ms : null,
      minSpeedDelta: ref ? row.min_speed - ref.min_speed : null,
      exitSpeedDelta: ref ? row.exit_speed - ref.exit_speed : null,
    };
  });
  return rows.sort((a, b) => {
    if (a.lostMs !== null && b.lostMs !== null) return b.lostMs - a.lostMs;
    if (a.lostMs !== null) return -1;
    if (b.lostMs !== null) return 1;
    return a.n - b.n;
  });
}

/** The corners where most time went, worst first, only those that lost any
 *  worth mentioning. */
export function worstCornerRows(rows: readonly CornerCompareRow[], limit = 3, floorMs = 20): CornerCompareRow[] {
  return rows
    .filter((row) => row.lostMs !== null && row.lostMs > floorMs)
    .sort((a, b) => (b.lostMs ?? 0) - (a.lostMs ?? 0))
    .slice(0, limit);
}

/** What the corners add up to: the sum of every delta the table shows. */
export function totalLostMs(rows: readonly CornerCompareRow[]): number | null {
  let total = 0;
  let any = false;
  for (const row of rows) {
    if (row.lostMs === null) continue;
    total += row.lostMs;
    any = true;
  }
  return any ? total : null;
}

/** The largest single loss or gain, which is what the bars are scaled to. */
export function cornerScale(rows: readonly CornerCompareRow[]): number {
  return rows.reduce((most, row) => Math.max(most, Math.abs(row.lostMs ?? 0)), 0);
}

/** `T3 R`, or the corner's name when somebody gave it one. */
export function cornerLabel(row: Pick<CornerCompareRow, 'n' | 'name' | 'direction'>): string {
  if (row.name) return row.name;
  return row.direction ? `T${row.n} ${row.direction}` : `T${row.n}`;
}

/** Where the brake went on, as a driver says it: metres before the apex. */
export function brakeBeforeApex(row: CornerCompareRow): number | null {
  if (row.row.brake_on === null || row.apexDist === null) return null;
  return row.apexDist - row.row.brake_on;
}

/** The last value of the delta trace. Not the gap at the line: the trace
 *  ends where the shorter recording ends and runs on the recordings' clocks,
 *  so for a lap that stopped short of the line, or whose clock ran past its
 *  time, it is the gap somewhere else. See `gapAtLineMs`. */
export function finalDeltaMs(delta: DeltaSeries | null | undefined): number | null {
  const last = delta?.delta_ms[delta.delta_ms.length - 1];
  return finite(last) ? last : null;
}

/** The gap at the line: the difference of the two lap times when both are
 *  known, which is what the timing table says; the trace's last value only
 *  for a lap that has no time. */
export function gapAtLineMs(
  lapMs: number | null | undefined,
  referenceMs: number | null | undefined,
  delta: DeltaSeries | null | undefined,
): number | null {
  if (finite(lapMs) && finite(referenceMs) && lapMs > 0 && referenceMs > 0) return lapMs - referenceMs;
  return finalDeltaMs(delta);
}

// ── consistency across laps ─────────────────────────────────────────────────

export const DEVIATION_BOX: Box = { width: 400, height: 110, padTop: 6, padBottom: 2 };

export interface DeviationDomain {
  maxDistance: number;
  low: number;
  high: number;
}

/** One scale for the median and its band, padded like the speed chart's. */
export function deviationDomain(data: Deviation | null | undefined): DeviationDomain | null {
  if (!data || data.dist.length < 2) return null;
  let maxDistance = 0;
  let low = Number.POSITIVE_INFINITY;
  let high = Number.NEGATIVE_INFINITY;
  const n = Math.min(data.dist.length, data.median.length, data.deviation.length);
  for (let i = 0; i < n; i += 1) {
    const metres = data.dist[i] as number;
    const median = data.median[i] as number;
    const spread = data.deviation[i] as number;
    if (!finite(metres) || !finite(median) || !finite(spread)) continue;
    if (metres > maxDistance) maxDistance = metres;
    if (median - spread < low) low = median - spread;
    if (median + spread > high) high = median + spread;
  }
  if (!Number.isFinite(low) || !Number.isFinite(high) || maxDistance <= 0) return null;
  const pad = Math.max((high - low) * 0.08, 1);
  return { maxDistance, low: Math.max(0, low - pad), high: high + pad };
}

/** The median as a line and the ±one-deviation band as a closed area. */
export function deviationPaths(
  data: Deviation,
  domain: DeviationDomain,
  box: Box = DEVIATION_BOX,
): { median: string; band: string } {
  const span = domain.high - domain.low || 1;
  const usable = box.height - box.padTop - box.padBottom;
  const y = (kmh: number) => (box.height - box.padBottom - ((kmh - domain.low) / span) * usable).toFixed(1);
  const n = Math.min(data.dist.length, data.median.length, data.deviation.length);
  const line: string[] = [];
  const upper: string[] = [];
  const lower: string[] = [];
  for (let i = 0; i < n; i += 1) {
    const metres = data.dist[i] as number;
    const median = data.median[i] as number;
    const spread = data.deviation[i] as number;
    if (!finite(metres) || !finite(median) || !finite(spread)) continue;
    const x = distanceX(metres, domain.maxDistance, box).toFixed(1);
    line.push(`${x},${y(median)}`);
    upper.push(`${x},${y(median + spread)}`);
    lower.push(`${x},${y(median - spread)}`);
  }
  if (line.length < 2) return { median: '', band: '' };
  return { median: line.join(' '), band: `M${upper.join(' L')} L${lower.reverse().join(' L')} Z` };
}

/** Where the laps disagree most: the stretches whose spread is in the top
 *  share, merged where they touch, worst first. */
export function widestSpreads(
  data: Deviation | null | undefined,
  limit = 3,
): Array<{ from: number; to: number; peak: number }> {
  if (!data || data.dist.length < 2) return [];
  const n = Math.min(data.dist.length, data.deviation.length);
  let most = 0;
  for (let i = 0; i < n; i += 1) most = Math.max(most, data.deviation[i] as number);
  if (most <= 0) return [];
  const floor = most * 0.6;
  const runs: Array<{ from: number; to: number; peak: number }> = [];
  let current: { from: number; to: number; peak: number } | null = null;
  for (let i = 0; i < n; i += 1) {
    const spread = data.deviation[i] as number;
    const metres = data.dist[i] as number;
    if (spread >= floor) {
      if (current) {
        current.to = metres;
        current.peak = Math.max(current.peak, spread);
      } else {
        current = { from: metres, to: metres, peak: spread };
      }
    } else if (current) {
      runs.push(current);
      current = null;
    }
  }
  if (current) runs.push(current);
  return runs.sort((a, b) => b.peak - a.peak).slice(0, limit);
}

// ── the g-g diagram ─────────────────────────────────────────────────────────

export interface GGPoint {
  /** Lateral g, positive to the right as the map draws it. */
  lat: number;
  /** Longitudinal g, positive under power. */
  long: number;
  /** 0 braking, 1 coasting, 2 on the throttle, as the logger colours them. */
  zone: 0 | 1 | 2;
}

/**
 * The lap's accelerometer in g, one point per step of the series. Null for a
 * recording without the channels, or a calibration the logger could not fit:
 * a confident-looking circle on an unproven scale is worse than none.
 */
export function ggPoints(lap: ComparedLap | null | undefined, accel: AccelCalibration | undefined): GGPoint[] | null {
  if (!lap || !accel?.available) return null;
  const lat = lap.series['acc_lat'];
  const long = lap.series['acc_long'];
  const kLat = accel.lateral.g_per_unit;
  const kLong = accel.longitudinal.g_per_unit;
  if (!lat || !long || !kLat || !kLong) return null;
  const throttle = lap.series['throttle'];
  const brake = lap.series['brake'];
  const n = Math.min(lat.length, long.length);
  const points: GGPoint[] = [];
  for (let i = 0; i < n; i += 1) {
    const a = lat[i];
    const b = long[i];
    if (!finite(a) || !finite(b)) continue;
    const brk = brake?.[i] ?? 0;
    const thr = throttle?.[i] ?? 0;
    points.push({ lat: a * kLat, long: b * kLong, zone: brk >= 1 ? 0 : thr >= 1 ? 2 : 1 });
  }
  return points.length > 0 ? points : null;
}

/** The scatter's half-width in g: the furthest point, rounded up to the
 *  next half g, never below one and a half. */
export function ggLimit(points: readonly GGPoint[]): number {
  let most = 0;
  for (const point of points) most = Math.max(most, Math.abs(point.lat), Math.abs(point.long));
  return Math.max(1.5, Math.ceil(most / 0.5) * 0.5);
}
